import "../variables.module.css";

import { Box } from "@dropbox/dig-foundations";
import { ScopedButtonRow } from "@mirage/mosaics/Omnibox/components/Omnibar/ScopedButtonRow/ScopedButtonRow";
import cx from "classnames";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import styles from "./Omnibar.module.css";

import type { typeahead } from "@mirage/service-typeahead-search/service/types";
import { isInsideExtensionSidePanel } from "@mirage/shared/util/webExtensionsHelpers";
import {
  MotionFlagValue,
  useOmniboxMotionFlag,
} from "../../hooks/useOmniboxMotion";

export type OmnibarProps = {
  // event handler for when we click the (+) sign to attach sources
  onAttachButtonClicked: () => void;
  // event handler for any of the scoped action buttons being clicked (find, ask, organize, create)
  onScopedButtonClicked: (mode: typeahead.Mode) => void;
  // whether the OmniboxResults (typeahed overlay) is open or not
  isTypeaheadOpen: boolean;
  // input row content
  children: React.ReactNode;
  // when true, omnibar will only render the omnibar. It will not render sources or scoped buttons
  isSlimMode?: boolean;
  // when true, omnibar is being used in full width, currently only in launcher mode
  fullWidth?: boolean;
  // the results list to render
  resultsList?: React.ReactNode;
};

export const Omnibar = forwardRef<HTMLDivElement, OmnibarProps>(
  (
    {
      onAttachButtonClicked,
      onScopedButtonClicked,
      isTypeaheadOpen,
      children,
      isSlimMode = false,
      fullWidth = false,
      resultsList,
    }: OmnibarProps,
    ref,
  ) => {
    const isFullMode = !isSlimMode && !fullWidth;

    const { initializingTracer, onStartTracer, motionTracerVersion } =
      useOmniboxTracer({
        isFullMode,
        isTypeaheadOpen,
      });

    return (
      <Box
        ref={ref}
        className={cx(styles.omnibarContainer, {
          [styles.isTypeaheadOpen]: isTypeaheadOpen,
          [styles.isSlimMode]: isSlimMode,
          [styles.fullWidth]: fullWidth,
          [styles.isFullMode]: isFullMode,
          [styles.isExtensionSidePanel]: isInsideExtensionSidePanel(),
          [styles.initializingTracer]: initializingTracer,
          [styles.isTracerMotionV1]: motionTracerVersion === "V1",
        })}
        onMouseEnter={onStartTracer}
      >
        {motionTracerVersion !== "OFF" && (
          <Tracer
            initializingTracer={initializingTracer}
            motionTracerVersion={motionTracerVersion}
          />
        )}

        {children}

        {isFullMode && (
          <ScopedButtonRow
            isTypeaheadOpen={isTypeaheadOpen}
            onAttachButtonClicked={onAttachButtonClicked}
            onScopedButtonClicked={onScopedButtonClicked}
          />
        )}

        {resultsList ? resultsList : null}
      </Box>
    );
  },
);
Omnibar.displayName = "Omnibar";

export function useOmniboxTracer({
  isFullMode,
  isTypeaheadOpen,
}: {
  isFullMode: boolean;
  isTypeaheadOpen: boolean;
}) {
  const hasTracerRun = useRef(false);
  const [initializingTracer, setInitializingTracer] = useState(false);
  const { getMotionVersionFor } = useOmniboxMotionFlag();
  const motionTracerVersion = getMotionVersionFor("tracer");

  useEffect(() => {
    if (!isTypeaheadOpen && !hasTracerRun.current) return;
    setInitializingTracer(false);
  }, [isTypeaheadOpen]);

  function onStartTracer() {
    if (!isFullMode || hasTracerRun.current || motionTracerVersion === "OFF")
      return;

    setInitializingTracer(true);
    hasTracerRun.current = true;
  }

  return { initializingTracer, onStartTracer, motionTracerVersion };
}

export function Tracer({
  initializingTracer,
  motionTracerVersion,
}: {
  initializingTracer: boolean;
  motionTracerVersion: MotionFlagValue | undefined;
}) {
  const OMNIBAR_HEIGHT = 130;

  return (
    <>
      <svg
        width="50%"
        height={OMNIBAR_HEIGHT}
        viewBox={`0 0 300 ${OMNIBAR_HEIGHT}`}
        className={cx(styles.tracerLeft, {
          [styles.isTracerMotionV1]: motionTracerVersion === "V1",
          [styles.initializingTracer]: initializingTracer,
        })}
      >
        <path
          d={`M 325 0 L -25 0 L -25 ${OMNIBAR_HEIGHT} L 325 ${OMNIBAR_HEIGHT}`}
          stroke="var(--dig-color__primary__base)"
          strokeWidth="80"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="830"
          strokeDashoffset="830"
        />
      </svg>
      <svg
        width="50%"
        height={OMNIBAR_HEIGHT}
        viewBox={`0 0 300 ${OMNIBAR_HEIGHT}`}
        className={cx(styles.tracerRight, {
          [styles.isTracerMotionV1]: motionTracerVersion === "V1",
          [styles.initializingTracer]: initializingTracer,
        })}
      >
        <path
          d={`M -25 0 L 325 0 L 325 ${OMNIBAR_HEIGHT} L -25 ${OMNIBAR_HEIGHT}`}
          stroke="var(--dig-color__primary__base)"
          strokeWidth="80"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="830"
          strokeDashoffset="830"
        />
      </svg>
    </>
  );
}
